import {
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { IMAGE_URL } from "network";
import { useEffect, useState } from "react";
import { uploadImage } from "services/imageUpload";

function AddStock({ open, onClose, initialData, onSubmit }) {
  const [previewUrl, setPreviewUrl] = useState("");

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      _id: initialData?._id || "",
      name: initialData?.name || "",
      image: initialData?.image || null,
      totalCount: initialData?.totalCount || "",
      remainingCount: initialData?.remainingCount || "",
      price: initialData?.price || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Product name is required"),
      image: Yup.mixed()
        .nullable()
        .test("fileRequired", "Image is required", function (value) {
          return initialData?.image || value;
        }),
      price: Yup.number()
        .typeError("Price must be a number")
        .required("Price is required")
        .integer("Price must be an integer")
        .min(0, "Price cannot be negative"),
      totalCount: Yup.number()
        .typeError("Total Stock must be a number")
        .required("Total Stock is required")
        .integer("Total Stock must be an integer")
        .min(0, "Total Stock cannot be negative"),
      remainingCount: Yup.number()
        .typeError("Remaining Stock must be a number")
        .required("Remaining Stock is required")
        .integer("Remaining Stock must be an integer")
        .min(0, "Remaining Stock cannot be negative")
        .test(
          "maxRemaining",
          "Remaining Stock cannot exceed Total Stock",
          function (value) {
            const { totalCount } = this.parent;
            return value <= totalCount;
          }
        ),
    }),
    onSubmit: async (values) => {
      await onSubmit(values);
      formik.resetForm();
    },
  });

  // Set initial preview if editing
  useEffect(() => {
    if (initialData?.image) {
      setPreviewUrl(IMAGE_URL + initialData.image);
    }
  }, [initialData]);

  // Handle file upload and preview
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageName = await uploadImage(file);
      setPreviewUrl(IMAGE_URL + imageName);
      formik.setFieldValue("image", imageName);
    }
  };

  // Auto-adjust remainingCount if totalCount decreases
  const handleTotalChange = (e) => {
    formik.handleChange(e);
    const total = Number(e.target.value);
    if (formik.values.remainingCount > total) {
      formik.setFieldValue("remainingCount", total);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialData ? "Edit Stock" : "Add Stock"}</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          <TextField
            name="name"
            label="Product Name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            fullWidth
            margin="normal"
          />

          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            Product Image
          </Typography>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ marginTop: 8 }}
          />
          {formik.touched.image && formik.errors.image && (
            <Typography color="error" variant="caption">
              {formik.errors.image}
            </Typography>
          )}

          {previewUrl && (
            <img
              crossOrigin="anonymous"
              src={previewUrl}
              alt="Preview"
              style={{
                marginTop: 16,
                width: "100px",
                maxHeight: 200,
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
          )}

          <TextField
            name="price"
            label="Price"
            value={formik.values.price}
            onChange={handleTotalChange}
            onBlur={formik.handleBlur}
            error={formik.touched.price && Boolean(formik.errors.price)}
            helperText={formik.touched.price && formik.errors.price}
            fullWidth
            margin="normal"
          />

          <TextField
            name="totalCount"
            label="Total Stock"
            value={formik.values.totalCount}
            onChange={handleTotalChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.totalCount && Boolean(formik.errors.totalCount)
            }
            helperText={formik.touched.totalCount && formik.errors.totalCount}
            fullWidth
            margin="normal"
          />

          <TextField
            name="remainingCount"
            label="Remaining Stock"
            value={formik.values.remainingCount}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.remainingCount &&
              Boolean(formik.errors.remainingCount)
            }
            helperText={
              formik.touched.remainingCount && formik.errors.remainingCount
            }
            fullWidth
            margin="normal"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained">
            {initialData ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AddStock;
